import React from "react";
import "@/index.css";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { RfqBasketProvider } from "@/context/RfqBasketContext";
import ProtectedRoute from "@/components/ProtectedRoute";

import Home from "@/pages/public/Home";
import About from "@/pages/public/About";
import Schools from "@/pages/public/Schools";
import Parents from "@/pages/public/Parents";
import Vendors from "@/pages/public/Vendors";
import Pricing from "@/pages/public/Pricing";
import Contact from "@/pages/public/Contact";
import Blog from "@/pages/public/Blog";
import Privacy from "@/pages/public/Privacy";
import Terms from "@/pages/public/Terms";
import Cookies from "@/pages/public/Cookies";
import NotFound from "@/pages/public/NotFound";

import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";

import SchoolDashboard from "@/pages/school/SchoolDashboard";
import Budget from "@/pages/school/Budget";
import Requisitions from "@/pages/school/Requisitions";
import RFQs from "@/pages/school/RFQs";
import PurchaseOrders from "@/pages/school/PurchaseOrders";
import SchoolVendors from "@/pages/school/Vendors";
import Reports from "@/pages/school/Reports";
import PlaceOrder from "@/pages/school/PlaceOrder";
import TrackOrders from "@/pages/school/TrackOrders";
import OrderHistory from "@/pages/school/OrderHistory";
import SchoolSettings from "@/pages/school/Settings";
import RfqHistory from "@/pages/school/RfqHistory";

import ParentDashboard from "@/pages/parent/ParentDashboard";
import Catalog from "@/pages/parent/Catalog";
import Cart from "@/pages/parent/Cart";
import Orders from "@/pages/parent/Orders";
import BookKits from "@/pages/parent/BookKits";

import VendorDashboard from "@/pages/vendor/VendorDashboard";
import SuperDashboard from "@/pages/super/SuperDashboard";
import SuperEnquiries from "@/pages/super/Enquiries";
import SuperProducts from "@/pages/super/Products";
import SuperSchools from "@/pages/super/Schools";
import SuperVendors from "@/pages/super/Vendors";
import SuperOrders from "@/pages/super/Orders";
import SuperFinance from "@/pages/super/Finance";
import SuperSettings from "@/pages/super/Settings";
import SuperBookKits from "@/pages/super/BookKits";
import SuperSchoolRfqs from "@/pages/super/SchoolRfqs";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import WhatsAppButton from "@/components/WhatsAppButton";

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
            <RfqBasketProvider>
            <Toaster position="top-right" richColors closeButton />
            <WhatsAppButton />
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/schools" element={<Schools />} />
              <Route path="/parents" element={<Parents />} />
              <Route path="/vendors" element={<Vendors />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/cookies" element={<Cookies />} />

              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />

              <Route path="/school/dashboard" element={<ProtectedRoute role="school_admin"><SchoolDashboard /></ProtectedRoute>} />
              <Route path="/school/order" element={<ProtectedRoute role="school_admin"><PlaceOrder /></ProtectedRoute>} />
              <Route path="/school/rfq-history" element={<ProtectedRoute role="school_admin"><RfqHistory /></ProtectedRoute>} />
              <Route path="/school/track" element={<ProtectedRoute role="school_admin"><TrackOrders /></ProtectedRoute>} />
              <Route path="/school/history" element={<ProtectedRoute role="school_admin"><OrderHistory /></ProtectedRoute>} />
              <Route path="/school/reports" element={<ProtectedRoute role="school_admin"><Reports /></ProtectedRoute>} />
              <Route path="/school/settings" element={<ProtectedRoute role="school_admin"><SchoolSettings /></ProtectedRoute>} />
              {/* Legacy routes kept for existing bookmarks + admin/future use — hidden from school nav. */}
              <Route path="/school/budget" element={<ProtectedRoute role="school_admin"><Budget /></ProtectedRoute>} />
              <Route path="/school/requisitions" element={<ProtectedRoute role="school_admin"><Requisitions /></ProtectedRoute>} />
              <Route path="/school/rfqs" element={<ProtectedRoute role="school_admin"><RFQs /></ProtectedRoute>} />
              <Route path="/school/pos" element={<ProtectedRoute role="school_admin"><PurchaseOrders /></ProtectedRoute>} />
              <Route path="/school/vendors" element={<ProtectedRoute role="school_admin"><SchoolVendors /></ProtectedRoute>} />

              <Route path="/parent/dashboard" element={<ProtectedRoute role="parent"><ParentDashboard /></ProtectedRoute>} />
              <Route path="/parent/book-kits" element={<ProtectedRoute role="parent"><BookKits /></ProtectedRoute>} />
              <Route path="/parent/catalog" element={<ProtectedRoute role="parent"><Catalog /></ProtectedRoute>} />
              <Route path="/parent/cart" element={<ProtectedRoute role="parent"><Cart /></ProtectedRoute>} />
              <Route path="/parent/orders" element={<ProtectedRoute role="parent"><Orders /></ProtectedRoute>} />

              <Route path="/vendor/dashboard" element={<ProtectedRoute role="vendor"><VendorDashboard /></ProtectedRoute>} />
              <Route path="/vendor/rfqs" element={<ProtectedRoute role="vendor"><VendorDashboard /></ProtectedRoute>} />
              <Route path="/vendor/pos" element={<ProtectedRoute role="vendor"><VendorDashboard /></ProtectedRoute>} />

              <Route path="/super/dashboard" element={<ProtectedRoute role="super_admin"><SuperDashboard /></ProtectedRoute>} />
              <Route path="/super/schools" element={<ProtectedRoute role="super_admin"><SuperSchools /></ProtectedRoute>} />
              <Route path="/super/products" element={<ProtectedRoute role="super_admin"><SuperProducts /></ProtectedRoute>} />
              <Route path="/super/book-kits" element={<ProtectedRoute role="super_admin"><SuperBookKits /></ProtectedRoute>} />
              <Route path="/super/school-rfqs" element={<ProtectedRoute role="super_admin"><SuperSchoolRfqs /></ProtectedRoute>} />
              <Route path="/super/vendors" element={<ProtectedRoute role="super_admin"><SuperVendors /></ProtectedRoute>} />
              <Route path="/super/orders" element={<ProtectedRoute role="super_admin"><SuperOrders /></ProtectedRoute>} />
              <Route path="/super/finance" element={<ProtectedRoute role="super_admin"><SuperFinance /></ProtectedRoute>} />
              <Route path="/super/enquiries" element={<ProtectedRoute role="super_admin"><SuperEnquiries /></ProtectedRoute>} />
              <Route path="/super/settings" element={<ProtectedRoute role="super_admin"><SuperSettings /></ProtectedRoute>} />

              {/* Legal-page aliases — legacy / commonly-typed URLs redirect
                  to the canonical documents so no external link ever 404s. */}
              <Route path="/cookie-policy" element={<Navigate to="/cookies" replace />} />
              <Route path="/terms-of-use" element={<Navigate to="/terms" replace />} />
              <Route path="/terms-and-conditions" element={<Navigate to="/terms" replace />} />
              <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />
              <Route path="/refund" element={<Navigate to="/terms" replace />} />
              <Route path="/refund-policy" element={<Navigate to="/terms" replace />} />
              <Route path="/cancellation-policy" element={<Navigate to="/terms" replace />} />

              {/* Catch-all — friendly 404 rather than a blank page */}
              <Route path="*" element={<NotFound />} />
            </Routes>
            </RfqBasketProvider>
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
